import { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { CloudUpload, Link2, Loader2, RefreshCw, Unplug } from "lucide-react";
import { api, formatApiErrorDetail, shopDateTime } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

const when = (value) => (value ? shopDateTime(value) : "Never");

export const DriveBackupPanel = () => {
  const [status, setStatus] = useState(null);
  const [busy, setBusy] = useState(false);
  const [params, setParams] = useSearchParams();
  const [confirming, setConfirming] = useState(false);
  const [password, setPassword] = useState("");

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/drive/status");
      setStatus(data);
    } catch {
      setStatus(null);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    const result = params.get("drive");
    if (!result) return;
    if (result === "connected") toast.success("Google Drive connected");
    else if (result === "expired") toast.error("That connection attempt timed out — try again");
    else toast.error("Google Drive could not be connected");
    params.delete("drive");
    setParams(params, { replace: true });
    load();
  }, [params, setParams, load]);

  useEffect(() => {
    if (!status?.syncing) return;
    const timer = setInterval(load, 5000);
    return () => clearInterval(timer);
  }, [status?.syncing, load]);

  const connect = async () => {
    setBusy(true);
    try {
      const { data } = await api.get("/drive/connect");
      window.location.href = data.authorization_url;
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
      setBusy(false);
    }
  };

  const act = async (path, message) => {
    setBusy(true);
    try {
      await api.post(path);
      toast.success(message);
      await load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  const disconnect = async () => {
    if (!password) return toast.error("Enter your password to confirm");
    setBusy(true);
    try {
      await api.post("/drive/disconnect", { password });
      toast.success("Google Drive disconnected");
      setConfirming(false);
      setPassword("");
      await load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  if (!status) return null;
  const manage = status.can_manage;

  return (
    <div className="border border-border p-5" data-testid="drive-backup-panel">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="max-w-2xl">
          <h2 className="text-base font-bold uppercase tracking-wider md:text-lg">
            Google Drive backup
          </h2>
          <p className="mt-1.5 text-sm text-muted-foreground">
            Every invoice, contract, loan, repair, KYC form and ID photo is copied into the
            shop's own Google Drive, under <strong>{status.folder}</strong>. Owner access only.
          </p>
        </div>
        <span
          className={`px-3 py-1 text-[11px] font-semibold uppercase tracking-widest ${
            status.connected ? "bg-[#1F6F43] text-white" : "bg-[#F1EDE4] text-[#6B5A32]"
          }`}
          data-testid="drive-status-badge"
        >
          {status.connected ? "Connected" : "Not connected"}
        </span>
      </div>

      {!status.configured && (
        <p
          className="mt-4 border border-[#C5A059] bg-[#FBF8F2] p-3 text-sm"
          data-testid="drive-setup-needed"
        >
          Setup needed: the shop's Google client ID and secret have not been added yet. Once
          they are in place, the Connect button below will take you to Google.
        </p>
      )}

      {status.connected && (
        <div className="mt-4 grid grid-cols-1 gap-px border border-border bg-border sm:grid-cols-3">
          {[
            ["Google account", status.account || "-", "account"],
            ["Last backup", when(status.last_sync_at), "last-sync"],
            ["Files copied", String(status.files_synced || 0), "file-count"],
            ["Nightly auto sync", "Every night at 9:30pm (Melbourne)", "nightly"],
          ].map(([label, value, key]) => (
            <div key={key} className="bg-white p-4" data-testid={`drive-${key}`}>
              <div className="text-[11px] font-semibold uppercase tracking-widest text-muted-foreground">
                {label}
              </div>
              <div className="mt-2 break-words text-sm font-semibold">{value}</div>
            </div>
          ))}
        </div>
      )}

      {status.last_error && (
        <p className="mt-3 text-sm text-[#A11B1B]" data-testid="drive-last-error">
          Last problem: {status.last_error}
        </p>
      )}

      {manage && (
        <div className="mt-5 flex flex-wrap gap-3">
          {!status.connected ? (
            <Button
              onClick={connect}
              disabled={busy || !status.configured}
              className="h-11 rounded-none bg-[#C5A059] px-6 font-semibold text-[#0A0A0A] hover:bg-[#B38F4E]"
              data-testid="connect-drive-button"
            >
              <Link2 className="mr-2 h-4 w-4" /> Connect Google Drive
            </Button>
          ) : (
            <>
              <Button
                onClick={() => act("/drive/sync", "Full backup started — this runs in the background")}
                disabled={busy || status.syncing}
                variant="outline"
                className="h-11 rounded-none border-[#C5A059] px-6 font-semibold"
                data-testid="drive-sync-now-button"
              >
                {status.syncing ? (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                ) : (
                  <RefreshCw className="mr-2 h-4 w-4" />
                )}
                {status.syncing ? "Syncing everything…" : "Sync everything now"}
              </Button>
              <Button
                onClick={() => {
                  setPassword("");
                  setConfirming(true);
                }}
                disabled={busy}
                variant="outline"
                className="h-11 rounded-none px-6"
                data-testid="drive-disconnect-button"
              >
                <Unplug className="mr-2 h-4 w-4" /> Disconnect
              </Button>
            </>
          )}
        </div>
      )}

      {confirming && (
        <div
          className="mt-5 border-2 border-destructive bg-[#FBF8F2] p-4"
          data-testid="drive-disconnect-confirm"
        >
          <p className="text-sm font-semibold">
            Disconnect Google Drive?
          </p>
          <p className="mt-1 text-xs text-muted-foreground">
            Backups stop immediately — nothing new is copied to Drive until you connect
            it again. Files already in Drive stay where they are. Enter your own password
            to confirm.
          </p>
          <div className="mt-3 flex flex-col gap-2 sm:flex-row sm:items-end">
            <div className="space-y-1.5">
              <Label className="text-[11px] uppercase tracking-wider">Your password</Label>
              <Input
                type="password"
                autoFocus
                className="h-10 max-w-[260px] rounded-none"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && disconnect()}
                data-testid="drive-disconnect-password"
              />
            </div>
            <Button
              onClick={disconnect}
              disabled={busy || !password}
              className="h-10 rounded-none bg-destructive px-5 font-semibold text-white hover:bg-destructive/90"
              data-testid="drive-disconnect-confirm-button"
            >
              {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
              Yes, disconnect
            </Button>
            <Button
              variant="outline"
              onClick={() => {
                setConfirming(false);
                setPassword("");
              }}
              className="h-10 rounded-none px-5"
              data-testid="drive-disconnect-cancel"
            >
              Keep it connected
            </Button>
          </div>
        </div>
      )}

      {!manage && (
        <p className="mt-4 flex items-center gap-2 text-sm text-muted-foreground">
          <CloudUpload className="h-4 w-4" /> Only the owner can change this connection.
        </p>
      )}
    </div>
  );
};
